import {
  getSchemaKind,
  isBlockNoteTemplate,
  isClinicOwnTemplate,
  isConsultationWithoutBlockNotes,
  isProtocolDocument,
  type DocumentTemplateSummary,
  type EncounterKind,
  type EncounterSchema,
  type SchemaDocumentRef,
} from '@/entities/templates';

import type { GalleryItem } from '../config';

type ProtocolEntry = {
  schema: EncounterSchema;
  kind: EncounterKind;
  reference: SchemaDocumentRef;
  template: DocumentTemplateSummary;
};

const getProtocolEntries = (
  schema: EncounterSchema,
  templates: DocumentTemplateSummary[],
): ProtocolEntry[] => {
  const kind = getSchemaKind(schema, templates);
  return schema.documents.flatMap((reference) => {
    const template = templates.find(
      (item) => item.series_id === reference.template_series_id,
    );
    return template && isProtocolDocument(reference, templates)
      ? [{ schema, kind, reference, template }]
      : [];
  });
};

const toProtocolCard = ({
  schema,
  kind,
  reference,
  template,
}: ProtocolEntry): GalleryItem => ({
  id: `protocol:${template.series_id}`,
  kind,
  name: template.name,
  description: template.description,
  schemaId: schema.id,
  templateSeriesId: template.series_id,
  onDemand: reference.inclusion !== 'always',
  blockNote: isBlockNoteTemplate(template),
  classification: template.classification,
  partner: template.partner_source?.partner,
  savedByClinic: isClinicOwnTemplate(template),
  schemaNames: [schema.name],
});

// One card per block note of any schema and per legacy protocol of a
// treatment schema; a consultation schema without block notes is one card of
// its own. A protocol linked by several schemas starts in the first and names
// each of them.
export const buildGalleryItems = ({
  schemas,
  templates,
}: {
  schemas: EncounterSchema[];
  templates: DocumentTemplateSummary[];
}): GalleryItem[] => {
  const entries = schemas.flatMap((schema) =>
    getProtocolEntries(schema, templates),
  );
  const consultations = schemas
    .filter((schema) => isConsultationWithoutBlockNotes(schema, templates))
    .map(
      (schema): GalleryItem => ({
        id: `schema:${schema.id}`,
        kind: 'consultation',
        name: schema.name,
        description: schema.description,
        schemaId: schema.id,
        blockNote: false,
        schemaNames: [],
        onDemand: false,
        savedByClinic: schema.documents.some(
          (reference) =>
            reference.inclusion === 'always' &&
            templates.some(
              (template) =>
                template.series_id === reference.template_series_id &&
                !template.partner_source &&
                isClinicOwnTemplate(template),
            ),
        ),
      }),
    );
  const protocols = entries
    .filter(
      ({ kind, template }) =>
        kind === 'treatment' || isBlockNoteTemplate(template),
    )
    .reduce<GalleryItem[]>((items, entry) => {
      const id = `protocol:${entry.template.series_id}`;
      return items.some((item) => item.id === id)
        ? items.map((item) =>
            item.id === id
              ? {
                  ...item,
                  schemaNames: [
                    ...new Set([...item.schemaNames, entry.schema.name]),
                  ],
                }
              : item,
          )
        : [...items, toProtocolCard(entry)];
    }, []);
  return [...consultations, ...protocols];
};

// The card a start page names: a series, or a schema without a note of its
// own.
export const findGalleryItem = (
  items: GalleryItem[],
  { seriesId, schemaId }: { seriesId?: string; schemaId?: string },
) =>
  items.find((item) =>
    seriesId
      ? item.templateSeriesId === seriesId
      : !item.templateSeriesId && item.schemaId === schemaId,
  );

// The server orders official consultation schemas before pagination.
export const sortGalleryItems = (items: GalleryItem[]) => [...items];

export const searchGalleryItems = (items: GalleryItem[], query: string) => {
  const needle = query.trim().toLocaleLowerCase();
  return needle
    ? items.filter((item) =>
        [item.name, item.description, ...item.schemaNames]
          .join(' ')
          .toLocaleLowerCase()
          .includes(needle),
      )
    : items;
};
